import { ApplicationConfig, importProvidersFrom } from '@angular/core';
import { provideRouter } from '@angular/router';
import { provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { provideAnimations } from '@angular/platform-browser/animations';
import { APP_INITIALIZER, LOCALE_ID, isDevMode } from '@angular/core';
import { HTTP_INTERCEPTORS } from '@angular/common/http';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { IonicModule, IonicRouteStrategy } from '@ionic/angular';
import { RouteReuseStrategy, TitleStrategy } from '@angular/router';
import { EffectsModule } from '@ngrx/effects';
import { StoreModule } from '@ngrx/store';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { AuthConfig, DateTimeProvider, OAuthModule, OAuthModuleConfig, OAuthStorage } from 'angular-oauth2-oidc';
import { NgxStripeModule } from 'ngx-stripe';
import { OwlNativeDateTimeModule } from '@danielmoncada/angular-datetime-picker';

import { routes } from './app-routing.module';
import { environment } from '../environments/environment';
import { authCodeFlowConfig } from './auth/configs/auth.config';
import { HeadersInterceptor } from './auth/http-interceptors/headers-interceptor';
import { ErrorLoggingInterceptor } from './auth/http-interceptors/error-logging-interceptor';
import { AuthService } from './auth/services/auth.service';
import { CURRENT_LOCALE_ID } from './shared/constants';
import { TemplatePageTitleStrategy } from './shared/services/template-page-title-strategy.strategy';
import { getAllowedUrls } from './utils/endpoints';
import { ServerDatetimeService } from './shared/services/server-datetime.service';
import { ServerDatetimeProvider } from './auth/date/server-datetime-provider';
import { ProfileMainHubService } from './shared/services/profile-main-hub.service';
import { Features, profileReducer, tournamentReducer } from './shared/store/appState';
import { commonSettingsReducer } from './shared/store/reducers/commonSettings.reducer';
import { mediaReducer } from './shared/store/reducers/media.reducer';
import { CommonSettingsEffect } from './shared/store/effects/commonSettings.effect';
import { MediaEffect } from './shared/store/effects/media.effect';
import { profileNotificationsReducer } from './notification-center/store/reducers/profileNotifications.reducer';
import { ProfileNotificationsEffect } from './notification-center/store/effects/profileNotifications.effect';
import { ProfileNotificationsService } from './notification-center/services/profile-notifications.service';
import { homeReducer } from './home/store/reducers/home.reducer';
import { HomeEffect } from './home/store/effects/home.effect';
import { walletReducer } from './wallet/store/reducers/wallet.reducer';
import { WalletEffect } from './wallet/store/effects/wallet.effect';
import { communicationChatReducer } from './communication-chat/store/reducers/communication-chat.reducer';
import { CommunicationChatEffect } from './communication-chat/store/effects/communication-chat.effect';
import { DateFormatPipe } from './shared/pipes/date-format.pipe';
import { CommunicationChatService } from './communication-chat/services/communication-chat.service';
import { TournamentBracketsEffect } from './tournament/store/effects/tournamentBrackets.effect';
import { TournamentGeneralEffect } from './tournament/store/effects/tournamentGeneral.effect';
import { TournamentParticipantsEffect } from './tournament/store/effects/tournamentParticipants.effect';
import { TournamentMatchesEffect } from './tournament/store/effects/tournamentMatches.effect';
import { profilePlayerTeamReducer } from './team/store/reducers/team.reducer';
import { TeamEffect } from './team/store/effects/team.effect';
import { CountryPipe } from './shared/pipes/country.pipe';
import { NotificationsService } from './shared/services/notifications.service';
import { ProfileGeneralEffect } from './profile/store/effects/profileGeneral.effect';
import { ProfilePlayerTournamentsEffect } from './profile/store/effects/profilePlayerTournaments.effect';
import { ProfileTeamsEffect } from './profile/store/effects/profileTeams.effect';
import { ProfileSettingsEffect } from './profile/store/effects/profileSettings.effect';
import { ProfileGeneralService } from './profile/services/profile-general.service';
import { InvitationService } from './profile/services/invitation.service';
import { ProfilePlayerTournamentsService } from './profile/services/profile-player-tournaments.service';
import { ProfileTeamsService } from './profile/services/profile-teams.service';
import { ModalWindowsService } from './modals/services/modal-windows.service';
import { leaderboardReducer } from './leaderboard/store/reducers/leaderboard.reducer';
import { LeaderboardEffect } from './leaderboard/store/effects/leaderboard.effect';
import { gamesReducer } from './games/store/reducers/games.reducer';
import { GamesEffect } from './games/store/effects/games.effect';
import { GamesService } from './games/services/games.service';
import { allTournamentsReducer } from './all-tournaments/store/reducers/allTournaments.reducer';
import { AllTournamentsEffect } from './all-tournaments/store/effects/allTournaments.effect';
import { AllTournamentsService } from './all-tournaments/services/all-tournaments.service';
import { PermissionsEffect } from './permissions/store/effects/permissions.effect';
import { PermissionsService } from './permissions/services/permissions.service';
import { permissionsReducer } from './permissions/store/reducers/permissions.reducer';
import { provideTranslateService } from '@ngx-translate/core';
import {provideTranslateHttpLoader} from '@ngx-translate/http-loader';
import { rewardsReducer } from './reward/store/reducers/reward.reducer';
import { RewardsEffects } from './reward/store/effects/reward.effect';
import { categoriesReducer } from './reward/store/reducers/categories.reducer';
import { CategoriesEffects } from './reward/store/effects/categories.effect';
import { claimReducer } from './reward/store/reducers/claim.reducer';
import { ClaimEffects } from './reward/store/effects/claim.effects';
import { seasonReducer } from './season/store/reducers/season.reducer';
import { SeasonEffects } from './season/store/effects/season.effects';
import { provideAngularSvgIcon } from 'angular-svg-icon';
import { provideServiceWorker } from '@angular/service-worker';


export function authAppInitializerFactory(
  authService: AuthService,
  timeService: ServerDatetimeService
) {
  return () => timeService
    .processTimeDifference()
    .then(() => authService.runInitialLoginSequence());
}

export const authModuleConfig: OAuthModuleConfig = {
  resourceServer: {
    allowedUrls: getAllowedUrls(),
    sendAccessToken: true,
  },
};

const storeFeatures = [
  StoreModule.forFeature(Features.CommonSettings, commonSettingsReducer),
  StoreModule.forFeature(Features.Media, mediaReducer),
  StoreModule.forFeature(Features.Notifications, profileNotificationsReducer),
  StoreModule.forFeature(Features.Home, homeReducer),
  StoreModule.forFeature(Features.Wallet, walletReducer),
  StoreModule.forFeature(Features.Chat, communicationChatReducer),
  StoreModule.forFeature(Features.Tournament, tournamentReducer),
  StoreModule.forFeature(Features.Team, profilePlayerTeamReducer),
  StoreModule.forFeature(Features.Profile, profileReducer),
  StoreModule.forFeature(Features.Leaderboard, leaderboardReducer),
  StoreModule.forFeature(Features.Games, gamesReducer),
  StoreModule.forFeature(Features.AllTournaments, allTournamentsReducer),
  StoreModule.forFeature(Features.Permissions, permissionsReducer),
  StoreModule.forFeature(Features.Rewards, rewardsReducer),
  StoreModule.forFeature(Features.Categories, categoriesReducer),
  StoreModule.forFeature(Features.ClaimReward, claimReducer),
  StoreModule.forFeature(Features.Season, seasonReducer),
];

const effectsFeatures = [
  CommonSettingsEffect,
  MediaEffect,
  ProfileNotificationsEffect,
  HomeEffect,
  RewardsEffects,
  CategoriesEffects,
  ClaimEffects,
  SeasonEffects,
  WalletEffect,
  CommunicationChatEffect,
  TournamentBracketsEffect,
  TournamentGeneralEffect,
  TournamentParticipantsEffect,
  TournamentMatchesEffect,
  TeamEffect,
  ProfileGeneralEffect,
  ProfilePlayerTournamentsEffect,
  ProfileTeamsEffect,
  ProfileSettingsEffect,
  LeaderboardEffect,
  GamesEffect,
  AllTournamentsEffect,
  PermissionsEffect
];

const services = [
    ServerDatetimeService,
    ProfileMainHubService,
    ProfileNotificationsService,
    CommunicationChatService,
    DateFormatPipe,

    CountryPipe,
    NotificationsService,
    ProfileGeneralService,
    InvitationService,
    ProfilePlayerTournamentsService,
    ProfileTeamsService,
    ModalWindowsService,
    GamesService,
    AllTournamentsService,
    PermissionsService
];

export const appConfig: ApplicationConfig = {
    providers: [
        importProvidersFrom(
            BrowserModule,
            BrowserAnimationsModule,
            IonicModule.forRoot(),
            StoreModule.forRoot({}),
            EffectsModule.forRoot([]),
            ...storeFeatures,
            EffectsModule.forFeature(effectsFeatures),
            StoreDevtoolsModule.instrument({
                maxAge: 25,
                logOnly: environment.production,
                autoPause: true,
            }),
            OAuthModule.forRoot({
                resourceServer: {
                    allowedUrls: getAllowedUrls(),
                    sendAccessToken: true,
                },
            }),
            OwlNativeDateTimeModule,
            NgxStripeModule.forRoot(environment.stripePublicKey),
        ),
        provideRouter(routes),
        provideAnimations(),
        provideHttpClient(withInterceptorsFromDi()),
        provideTranslateService({
          loader: provideTranslateHttpLoader({prefix:"./assets/i18n/", suffix:".json"}),
          fallbackLang: 'en'}),{ provide: AuthConfig, useValue: authCodeFlowConfig },
        { provide: OAuthModuleConfig, useValue: authModuleConfig },
        { provide: OAuthStorage, useValue: localStorage },
        { provide: DateTimeProvider, useClass: ServerDatetimeProvider },
        { provide: RouteReuseStrategy, useClass: IonicRouteStrategy },
        { provide: HTTP_INTERCEPTORS, useClass: HeadersInterceptor, multi: true },
        { provide: HTTP_INTERCEPTORS, useClass: ErrorLoggingInterceptor, multi: true },
        {
            provide: APP_INITIALIZER,
            useFactory: authAppInitializerFactory,
            deps: [AuthService, ServerDatetimeService],
            multi: true,
        },
        { provide: TitleStrategy, useClass: TemplatePageTitleStrategy },
        { provide: LOCALE_ID, useValue: CURRENT_LOCALE_ID },
        ...services,
        provideTranslateService({
            loader: provideTranslateHttpLoader({prefix:"./assets/i18n/", suffix:".json"})}),
        provideAngularSvgIcon(), provideServiceWorker('ngsw-worker.js', {
            enabled: !isDevMode(),
            registrationStrategy: 'registerWhenStable:30000'
          })
    ],
};
